import {
  ClassDetailsNoSectionsQuery,
  ListingCard_ListingFragment,
  OtherListingsByTeacherQuery as OtherListingsByTeacherQueryType,
  OtherListingsByTeacherQueryVariables,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import {
  OtherListingsByTeacherQuery,
  OtherListingsByTeacherVariable,
  getActivityUpsellListing,
} from "@outschool/ui-components-website";
import { PageWidthSection } from "@outschool/ui-legacy-component-library";
import React from "react";

import {
  BundleElementType,
  ClassLinkCarousel,
  ClassLinkType,
} from "./ClassLinkCarousel";
import ListingCarousel from "./ListingCarousel";

export default function OtherListingsByTeacherCarousel({
  activity,
  leaderName,
  carouselRowNum,
  currentUserIsEnrolled = false,
}: {
  activity: ClassDetailsNoSectionsQuery["activity"];
  leaderName?: string;
  carouselRowNum: number;
  currentUserIsEnrolled?: boolean;
}) {
  const { t } = useTranslation("client\\components\\listings\\carousels");

  const variables = React.useMemo(
    () => OtherListingsByTeacherVariable(activity),
    [activity],
  );

  const { data } = useQueryWithPreviousData<
    OtherListingsByTeacherQueryType,
    OtherListingsByTeacherQueryVariables
  >(OtherListingsByTeacherQuery, variables);

  const allListings: ListingCard_ListingFragment[] | undefined =
    data?.listing?.similarListings?.sameTeacher;

  const upsellListing = React.useMemo(() => {
    if (!allListings?.length) {
      return [];
    }

    return getActivityUpsellListing(allListings, activity);
  }, [allListings, activity]) as ListingCard_ListingFragment | undefined;

  if (!allListings?.length) {
    return null;
  }

  const hasUpsellListing =
    allListings[0].activity.uid === activity.upsell_activity_uid;

  const otherListings = hasUpsellListing ? allListings.slice(1) : allListings;

  function getFeaturedClassLink(
    activity: ClassDetailsNoSectionsQuery["activity"],
    isOrdered: boolean,
  ): ClassLinkType | null {
    if (!activity.leader.leaderClassLinks) {
      return null;
    }

    const allLinks = activity.leader.leaderClassLinks;
    // filtering to only the links that are the right type
    // then only the links that include the given activity
    const filteredLinks = allLinks
      .filter((link: ClassLinkType) => link.isOrdered == isOrdered)
      .filter((link: ClassLinkType) =>
        link.bundleElements
          .map((el: BundleElementType) => el.activity.uid)
          .includes(activity.uid),
      );
    return filteredLinks.sort(
      (a: ClassLinkType, b: ClassLinkType) =>
        b.bundleElements.length - a.bundleElements.length,
    )[0];
  }

  const featuredSequence = getFeaturedClassLink(activity, true);
  const featuredBundle = getFeaturedClassLink(activity, false);

  return (
    <PageWidthSection>
      {featuredSequence ? (
        <ClassLinkCarousel
          classLink={featuredSequence}
          currentActivityUid={activity.uid}
          carouselRowNum={carouselRowNum}
        />
      ) : upsellListing ? (
        <ListingCarousel
          sx={{
            padding: 0,
          }}
          alwaysShown
          listings={[upsellListing]}
          trackingLabel={"listing_card"}
          trackingProperties={{
            carousel_type: "other_listings_by_teacher",
            carousel_subtype: "upsell_class",
            currentUserIsEnrolled,
          }}
          carouselRowNum={carouselRowNum}
          hideTeacher
          showSummary
          title={t("{{leaderName}} suggests this class next", { leaderName })}
          leftAlign
          headingSx={{ textAlign: "left" }}
        />
      ) : null}
      {featuredBundle && (
        <ClassLinkCarousel
          classLink={featuredBundle}
          currentActivityUid={activity.uid}
          carouselRowNum={carouselRowNum}
        />
      )}
      {Boolean(otherListings.length) && (
        <ListingCarousel
          sx={{
            padding: 0,
          }}
          listings={otherListings}
          title={t("Other classes by {{leaderName}}", { leaderName })}
          hideTeacher
          trackingLabel={"listing_card"}
          trackingProperties={{
            carousel_type: "other_listings_by_teacher",
            carousel_subtype: "other_listings",
            currentUserIsEnrolled,
          }}
          carouselRowNum={carouselRowNum}
          leftAlign
          headingSx={{ textAlign: "left" }}
        />
      )}
    </PageWidthSection>
  );
}
